import type { LocatesWorkerContract } from './contract';
import { LocateOrderStatus } from '@thinkalpha/ui-types/contracts/locates/orders';
import type { LocateOrder } from '@thinkalpha/ui-types/contracts/locates/orders';
import type { FlattenedLocateResultRow } from '@thinkalpha/ui-types/types/labModern';
import type { WorkerContractSchemas } from 'src/services/workerHost/contract';
import { workerSessionSnapshotSchema } from 'src/services/workerHost/stateRegistry';
import { z } from 'zod';

const instant = workerSessionSnapshotSchema.shape.superuserAccessEnabledUntil.unwrap();
const optionalString = z.string().optional();
const nullableString = z.string().nullable();
const nullableNumber = z.number().nullable();
export const locateOrderSchema = z.object({
    id: z.string(),
    orderId: z.string(),
    optimisticOrderId: optionalString,
    createdTime: instant,
    lastUpdateTime: instant,
    offerId: optionalString,
    symbol: z.string(),
    quantity: z.number(),
    price: z.number().optional(),
    accountId: optionalString,
    requestedProviderId: optionalString,
    resolvedProviderId: optionalString,
    orderStatus: z.enum(LocateOrderStatus),
    rejectReason: optionalString,
    borrowStatus: optionalString,
    inventory: z.object({ total: nullableNumber }).catchall(nullableNumber).optional(),
    autoAccept: z
        .discriminatedUnion('enabled', [
            z.object({ enabled: z.literal(false), price: z.number().optional() }),
            z.object({
                enabled: z.literal(true),
                price: z.number(),
                triggers: z
                    .array(
                        z.discriminatedUnion('type', [
                            z.object({ type: z.literal('locate-rate'), maxRate: z.number() }),
                            z.object({
                                type: z.literal('security-price'),
                                maxRatePercent: z.number(),
                                priceSource: z.enum(['last', 'bid', 'ask', 'mid']).optional(),
                            }),
                        ]),
                    )
                    .optional(),
            }),
        ])
        .optional(),
    requestingUserId: optionalString,
    userId: optionalString,
    clientTrackingId: optionalString,
}) satisfies z.ZodType<LocateOrder>;
export const locateRowSchema = z.object({
    id: z.string(),
    type: z.literal('data'),
    orderId: z.string(),
    resultId: z.string(),
    stableOrderId: z.string(),
    symbol: z.string(),
    accountId: z.string(),
    createdAt: instant,
    updatedAt: instant.optional(),
    createdBy: z.string(),
    userId: optionalString,
    marketParticipantId: z.string(),
    orderQuantity: z.number(),
    borrowStatus: z.string(),
    routeName: nullableString,
    offeredQuantity: nullableNumber,
    price: nullableNumber,
    billingPrice: nullableNumber,
    providerId: z.string(),
    resultState: z.string(),
    acceptedAt: instant.nullable(),
    actionableAt: instant.nullable(),
    rejectedAt: instant.nullable(),
    rejectedBy: nullableString,
    rejectReason: nullableString,
    injectionTargetName: optionalString,
    integration: nullableString,
    clientTrackingId: nullableString,
    offerId: optionalString,
    autoAcceptEnabled: z.boolean().optional(),
    autoAcceptPrice: z.number().optional(),
    autoAcceptType: z.enum(['price', 'percent']).optional(),
    parity: z.enum(['odd', 'even']).optional(),
}) satisfies z.ZodType<FlattenedLocateResultRow>;
const sort = z.enum([
    'createdAt desc',
    'createdAt asc',
    'symbol desc',
    'symbol asc',
    'lastUpdateTime desc',
    'lastUpdateTime asc',
    'price desc',
    'price asc',
    'quantity desc',
    'quantity asc',
    'account desc',
    'account asc',
    'borrowStatus desc',
    'borrowStatus asc',
    'provider desc',
    'provider asc',
    'totalCost desc',
    'totalCost asc',
]);
const filters = z.object({
    textSearch: nullableString,
    onlyPending: z.boolean(),
    onlyResults: z.boolean(),
    onlyUserActivity: z.boolean(),
    currentUserId: nullableString,
    selectedAccountIds: z.array(z.string()).nullable(),
    columnFilters: z.record(z.string(), z.string()).optional(),
});
const empty = z.object({});
export const locatesWorkerSchemas = {
    requests: {
        updateProviders: {
            request: z.object({
                providers: z.array(
                    z.object({
                        id: z.string(),
                        publicName: z.string(),
                        privateName: optionalString,
                        supportsPreBorrows: z.boolean(),
                        supportsQuotes: z.boolean(),
                        groups: z.array(z.string()).optional(),
                        isCompound: z.boolean(),
                        isInjectable: z.boolean(),
                        isHidden: z.boolean(),
                        sortOrder: z.number(),
                    }),
                ),
            }),
            response: z.object({ type: z.literal('updateProviders'), success: z.boolean() }),
        },
        initialFetch: {
            request: z.object({ limit: z.number().optional() }),
            response: z.object({ type: z.literal('initialFetchComplete'), orderCount: z.number() }),
        },
        upsertOrders: {
            request: z.object({ orders: z.array(locateOrderSchema) }),
            response: z.object({ type: z.literal('ordersUpserted'), success: z.boolean() }),
        },
        socketUpdate: {
            request: z.object({ order: locateOrderSchema }),
            response: z.object({ type: z.literal('socketUpdateComplete') }),
        },
        query: {
            request: z.object({
                direction: z.enum(['initial', 'up', 'down']),
                limit: z.number(),
                anchorOffset: z.number().optional(),
                anchorStableOrderId: optionalString,
                fallbackAnchorOffset: z.number().optional(),
                fallbackAnchorStableOrderIds: z.array(z.string()).optional(),
                includeAnchor: z.boolean().optional(),
            }),
            response: z.object({
                type: z.literal('queryResult'),
                rows: z.array(locateRowSchema),
                isStartOfData: z.boolean(),
                isEndOfData: z.boolean(),
            }),
        },
        updateSort: { request: z.object({ sort }), response: z.object({ type: z.literal('sortUpdateComplete') }) },
        updateFilters: {
            request: z.object({ filters }),
            response: z.object({ type: z.literal('filtersUpdateComplete') }),
        },
        socketUpdateWithRequery: {
            request: z.object({ order: locateOrderSchema, anchorStableOrderId: nullableString, limit: z.number() }),
            response: z.object({
                type: z.literal('socketUpdateWithRequeryResult'),
                rows: z.array(locateRowSchema),
                isEndOfDataUpward: z.boolean(),
                isEndOfDataDownward: z.boolean(),
            }),
        },
        initializeSocket: {
            request: z.object({
                connectionSettings: z.object({ uri: z.string() }),
                accountIds: z.array(z.string()),
                instanceId: z.string(),
            }),
            response: z.object({ type: z.literal('socketInitialized'), success: z.boolean() }),
        },
        exportCsv: {
            request: z.object({
                columnOrder: z.array(z.string()),
                columnVisibility: z.record(z.string(), z.boolean()),
            }),
            response: z.discriminatedUnion('success', [
                z.object({
                    type: z.literal('exportCsvResult'),
                    success: z.literal(true),
                    csvData: z.string(),
                    rowCount: z.number(),
                }),
                z.object({ type: z.literal('exportCsvResult'), success: z.literal(false), error: z.string() }),
            ]),
        },
    },
    events: {
        rowCountsChanged: z.object({
            filteredCount: z.number().int().nonnegative(),
            totalCount: z.number().int().nonnegative(),
        }),
        filterCacheInvalidated: empty,
        ordersReset: empty,
        socketConnectionStatus: z.object({
            status: z.enum(['uninitialized', 'connecting', 'connected', 'disconnected']),
        }),
        locatesActivity: z.object({
            activity: z.object({
                id: z.string(),
                accountId: z.string(),
                symbol: z.string(),
                resultState: z.enum(['accepted', 'rejected']),
                timestamp: instant,
                orderQuantity: z.number(),
                price: nullableNumber,
                providerId: z.string(),
                rejectReason: nullableString,
                activityUserId: nullableString,
            }),
        }),
    },
} satisfies WorkerContractSchemas<LocatesWorkerContract>;
